import { useCallback, useEffect, useRef, useState } from 'react';
import { api, type ChannelAccess, type ConversationView, type WebhookList, type WebhookView } from '../../api/client';
import { Button, Chip, Field, Loading, Notice } from '../ui';
import { Cropper } from '../PhotoEditor';
import { fullWhen, plural } from '../../lib/format';
import { Icon, type IconName } from '../../lib/icons';
import { pictureFiles } from '../../lib/pictures';
import { initials } from '../../lib/session';
import { Avatar } from './Avatar';

/**
 * Channels for everyone, open ones and private ones (1.12.19), and a channel's webhooks: the
 * pieces Messages.tsx puts in its dialogs (New channel, Channel settings, People here).
 */

export const KIND_CHOICES: { access: ChannelAccess; label: string; icon: IconName; hint: (company: string) => string }[] = [
  { access: 'open', label: 'Open', icon: 'hash', hint: (company) => `Anyone at ${company} can find it and join. People can leave.` },
  { access: 'private', label: 'Private', icon: 'lock', hint: () => 'Only the people in it can see it: not even admins, unless they’re in it. Anyone in it can add people.' },
  { access: 'everyone', label: 'Everyone', icon: 'users', hint: (company) => `Everyone at ${company} is in it, people who join later too. Nobody can leave it; they can mute it.` }
];

/** A channel's icon: a lock for a private one, # for the rest. */
export function channelIcon(access: ChannelAccess | null | undefined): IconName {
  return access === 'private' ? 'lock' : 'hash';
}

const errorText = (e: unknown) => (e instanceof Error ? e.message : String(e));

/** Choose people from the company (not those in `exclude`). */
export function ChannelPeoplePicker({ domain, exclude = [], chosen, onChange }: { domain: string; exclude?: string[]; chosen: string[]; onChange: (ids: string[]) => void }) {
  const [people, setPeople] = useState<{ id: string; name: string; extension: string | null; me: boolean }[]>();
  const [filter, setFilter] = useState('');
  const [error, setError] = useState('');
  useEffect(() => {
    api
      .messagePeople(domain)
      .then((r) => setPeople(r.people.filter((p) => !p.me)))
      .catch((e) => setError(errorText(e)));
  }, [domain]);
  const out = new Set(exclude);
  const shown = (people ?? []).filter((p) => !out.has(p.id) && (!filter.trim() || `${p.name} ${p.extension ?? ''}`.toLowerCase().includes(filter.trim().toLowerCase())));
  return (
    <div className="stack">
      <Field label="Find people" type="search" value={filter} placeholder="Name or extension" onChange={(e) => setFilter(e.target.value)} />
      {!people && !error && <Loading label="Loading people" />}
      {error && <Notice tone="crit">{error}</Notice>}
      {people && !shown.length && <p className="muted">{filter ? 'Nobody by that name.' : 'Everyone is in it already.'}</p>}
      <div className="msg-pick" role="group" aria-label="People">
        {shown.map((p) => (
          <label key={p.id} className="msg-pick-row">
            <input type="checkbox" checked={chosen.includes(p.id)} onChange={(e) => onChange(e.target.checked ? [...chosen, p.id] : chosen.filter((x) => x !== p.id))} />
            <Avatar id={p.id} name={p.name} small />
            <span className="grow">{p.name}</span>
            {p.extension && <span className="muted mono">Ext {p.extension}</span>}
          </label>
        ))}
      </div>
    </div>
  );
}

/** Who a channel is for: radio choices with what each means. */
export function KindChoice({ value, onChange, company, disabled }: { value: ChannelAccess; onChange: (a: ChannelAccess) => void; company: string; disabled?: boolean }) {
  return (
    <fieldset className="msg-choices msg-kinds" disabled={disabled}>
      <legend>Who it’s for</legend>
      {KIND_CHOICES.map((k) => (
        <label key={k.access} className="msg-choice">
          <input type="radio" name="msg-kind" checked={value === k.access} onChange={() => onChange(k.access)} />
          <span className="msg-kind-icon" aria-hidden="true">
            <Icon name={k.icon} size={15} />
          </span>
          <span className="grow">
            <strong>{k.label}</strong>
            <span className="muted">{k.hint(company)}</span>
          </span>
        </label>
      ))}
    </fieldset>
  );
}

// --- webhooks ------------------------------------------------------------------------------------

/** Copies an address; says so (or selects it, where the browser won't). */
function CopyAddress({ url }: { url: string }) {
  const [done, setDone] = useState('');
  const box = useRef<HTMLInputElement>(null);
  return (
    // Private: live help and session recording never see a webhook's address (it's a password).
    <div className="msg-hook-address" data-private="">
      <input ref={box} className="input mono" readOnly value={url} aria-label="The webhook’s address" onFocus={(e) => e.currentTarget.select()} />
      <Button
        size="small"
        icon="copy"
        onClick={async () => {
          try {
            await navigator.clipboard.writeText(url);
            setDone('Copied');
          } catch {
            box.current?.select();
            setDone('Selected: copy it');
          }
          window.setTimeout(() => setDone(''), 2500);
        }}
      >
        {done || 'Copy'}
      </Button>
    </div>
  );
}

/** A webhook's picture: chosen and cropped as profile pictures are; the 150 × 150 one goes to Stitch. */
function HookPicture({ domain, channel, hook, onSaved }: { domain: string; channel: string; hook: WebhookView; onSaved: (w: WebhookView) => void }) {
  const [src, setSrc] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const input = useRef<HTMLInputElement>(null);
  useEffect(() => () => void (src && URL.revokeObjectURL(src)), [src]);
  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError('');
    try {
      await fn();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  if (src) {
    return (
      <div className="msg-hook-crop">
        <Cropper
          src={src}
          busy={busy}
          onCancel={() => setSrc(null)}
          onSave={(img, crop) =>
            void run(async () => {
              const files = await pictureFiles(img, crop);
              onSaved((await api.webhookPicture(domain, channel, hook.id, files.small)).webhook);
              setSrc(null);
            })
          }
        />
        {error && <Notice tone="crit">{error}</Notice>}
      </div>
    );
  }
  return (
    <span className="msg-hook-face">
      <input ref={input} type="file" accept="image/*" hidden onChange={(e) => (e.target.files?.[0] && setSrc(URL.createObjectURL(e.target.files[0])), (e.target.value = ''))} />
      <button type="button" className="msg-avatar from-app has-action" aria-label={hook.picture ? `Change ${hook.name}’s picture` : `Add a picture for ${hook.name}`} title={hook.picture ? 'Change the picture' : 'Add a picture'} onClick={() => input.current?.click()} disabled={busy}>
        {hook.picture ? <img src={api.webhookPictureUrl(domain, channel, hook.id, hook.picture)} alt="" /> : initials(hook.name)}
      </button>
      {hook.picture && (
        <button type="button" className="link-btn small-print" onClick={() => void run(async () => onSaved((await api.webhookPicture(domain, channel, hook.id, null)).webhook))} disabled={busy}>
          Remove picture
        </button>
      )}
      {error && <span className="msg-status failed">{error}</span>}
    </span>
  );
}

const STATE: Record<WebhookView['state'], { label: string; tone: 'ok' | 'warn' | 'neutral' }> = {
  active: { label: 'On', tone: 'ok' },
  paused: { label: 'Paused', tone: 'warn' },
  archived: { label: 'Channel archived', tone: 'neutral' }
};

function HookRow({ domain, c, hook, showOnce, onChanged, onGone }: { domain: string; c: ConversationView; hook: WebhookView; showOnce: boolean; onChanged: (w: WebhookView, fresh?: boolean) => void; onGone: () => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [asking, setAsking] = useState<'' | 'replace' | 'delete'>('');
  const act = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError('');
    try {
      await fn();
      setAsking('');
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  const state = STATE[hook.state];
  return (
    <li className="msg-hook">
      <div className="msg-hook-head">
        <HookPicture domain={domain} channel={c.id} hook={hook} onSaved={(w) => onChanged(w)} />
        <span className="grow cell-stack">
          <strong>{hook.name}</strong>
          <span className="muted small-print">
            {hook.lastUsedAt ? `Last message ${fullWhen(hook.lastUsedAt)}` : 'No messages yet'} · {plural(hook.today, 'message')} today
          </span>
        </span>
        <Chip tone={state.tone} small>
          {state.label}
        </Chip>
      </div>
      {hook.url ? (
        <>
          <CopyAddress url={hook.url} />
          {showOnce && <p className="muted small-print">Copy it now: Stitch can’t show this address again.</p>}
        </>
      ) : (
        <p className="muted small-print">Its address was shown when it was made. Replace it to get a new one.</p>
      )}
      {hook.state === 'archived' && <Notice tone="warn">#{c.title} is archived, so messages sent to this webhook are turned away (410). Unarchive the channel to take them again.</Notice>}
      {hook.lastError && (
        <p className="msg-hook-error">
          <Icon name="alert" size={13} /> {hook.lastError.message} <span className="muted">({fullWhen(hook.lastError.at)})</span>
        </p>
      )}
      {asking === 'replace' ? (
        <Notice tone="warn" title="Replace the address?">
          The old address stops working at once. Give the new one to {hook.name}.{' '}
          <span className="msg-hook-confirm">
            <Button size="small" variant="danger" disabled={busy} onClick={() => void act(async () => onChanged((await api.replaceWebhook(domain, c.id, hook.id)).webhook, true))}>
              Replace
            </Button>
            <Button size="small" variant="ghost" onClick={() => setAsking('')}>
              Keep it
            </Button>
          </span>
        </Notice>
      ) : asking === 'delete' ? (
        <Notice tone="warn" title={`Delete ${hook.name}?`}>
          Its address stops working at once. Messages it already posted stay.{' '}
          <span className="msg-hook-confirm">
            <Button size="small" variant="danger" disabled={busy} onClick={() => void act(async () => (await api.deleteWebhook(domain, c.id, hook.id), onGone()))}>
              Delete
            </Button>
            <Button size="small" variant="ghost" onClick={() => setAsking('')}>
              Keep it
            </Button>
          </span>
        </Notice>
      ) : (
        <div className="msg-hook-actions">
          {hook.state !== 'archived' && (
            <Button size="small" variant="ghost" icon={hook.state === 'paused' ? 'play' : 'pause'} disabled={busy} onClick={() => void act(async () => onChanged((await api.changeWebhook(domain, c.id, hook.id, { paused: hook.state !== 'paused' })).webhook))}>
              {hook.state === 'paused' ? 'Resume' : 'Pause'}
            </Button>
          )}
          <Button size="small" variant="ghost" icon="refresh" disabled={busy} onClick={() => setAsking('replace')}>
            Replace address
          </Button>
          <Button size="small" variant="ghost" icon="trash" disabled={busy} onClick={() => setAsking('delete')}>
            Delete
          </Button>
        </div>
      )}
      {error && <Notice tone="crit">{error}</Notice>}
    </li>
  );
}

/** Channel settings → Webhooks: outside services post into the channel (Slack's format, or Stitch's own). */
export function ChannelWebhooks({ domain, c }: { domain: string; c: ConversationView }) {
  const [data, setData] = useState<WebhookList>();
  const [error, setError] = useState('');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  /** Addresses shown once (no data key): kept on screen until the dialog closes. */
  const [fresh, setFresh] = useState<Record<string, string>>({});
  const load = useCallback(async () => {
    try {
      setData(await api.webhooks(domain, c.id));
      setError('');
    } catch (e) {
      setError(errorText(e));
    }
  }, [domain, c.id]);
  useEffect(() => {
    void load();
  }, [load]);
  const changed = (w: WebhookView, isNew = false) => {
    if (isNew && w.url) setFresh((f) => ({ ...f, [w.id]: w.url! }));
    setData((d) => (d ? { ...d, webhooks: d.webhooks.some((x) => x.id === w.id) ? d.webhooks.map((x) => (x.id === w.id ? w : x)) : [...d.webhooks, w] } : d));
  };
  const add = async () => {
    setBusy(true);
    setError('');
    try {
      changed((await api.addWebhook(domain, c.id, name.trim())).webhook, true);
      setName('');
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  if (!data && !error) return <Loading label="Loading webhooks" />;
  return (
    <div className="stack msg-hooks" data-private="">
      <p className="muted">
        Services that post to Slack (status pages, monitoring, builds) can post here instead: give them the address. Messages show as from the webhook, with an <strong>App</strong> label.
      </p>
      {data && data.webhooks.length > 0 && (
        <ul className="msg-hook-list">
          {data.webhooks.map((h) => (
            <HookRow
              key={h.id}
              domain={domain}
              c={c}
              hook={fresh[h.id] && !h.url ? { ...h, url: fresh[h.id]! } : h}
              showOnce={Boolean(fresh[h.id]) && !data.canShowAgain}
              onChanged={changed}
              onGone={() => setData((d) => (d ? { ...d, webhooks: d.webhooks.filter((x) => x.id !== h.id) } : d))}
            />
          ))}
        </ul>
      )}
      {data && !data.archived && (
        <form
          className="msg-hook-add"
          onSubmit={(e) => {
            e.preventDefault();
            if (name.trim()) void add();
          }}
        >
          <Field label="Add a webhook" value={name} maxLength={80} placeholder="Statuspage" hint="The name its messages show." onChange={(e) => setName(e.target.value)} />
          <Button type="submit" variant="primary" icon="plus" disabled={busy || !name.trim()}>
            Add webhook
          </Button>
        </form>
      )}
      {data?.archived && <Notice tone="warn">#{c.title} is archived: its webhooks are turned away until it’s unarchived.</Notice>}
      {error && <Notice tone="crit">{error}</Notice>}
      <details className="msg-hook-help">
        <summary>What to send</summary>
        <p>
          Anything a service sends to Slack’s incoming webhooks works as it is. Or send Stitch’s own: <code>{'{ "title": "Disk almost full", "status": "warning", "text": "Volume /var is at **94%**." }'}</code>
        </p>
        <p className="muted small-print">The address is the only key: keep it private, and replace it if it gets out. More in Help → Messages → Webhooks.</p>
      </details>
    </div>
  );
}
